import * as React from 'react';
import { Link } from 'react-router-dom';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getSuperAdminUser,
  getSuperAdminUserAvailableNotificationChannelSchools,
  createSuperAdminUserNotificationChannel,
  deleteSuperAdminUserNotificationChannel,
  SuperAdminUser as SuperAdminUserModel,
  SuperAdminUserNotificationChannel,
  setSuperAdminUserBlock,
  setSuperAdminUserEmailVerification,
  setSuperAdminUserSmsVerification,
  updateSuperAdminUser,
  SuperAdminUserUpdateData,
  getSuperAdminLoginMergeCandidates,
  previewSuperAdminLoginMerge,
  mergeSuperAdminLoginAccount,
  SuperAdminLoginMergePreview
} from 'Src/helpers/service/superadmin/users';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';
import { SuperAdminUserPermissions } from './Permissions/SuperAdminUserPermissions';
import { SuperAdminUserSessions } from './Sessions/SuperAdminUserSessions';
import { SuperAdminUserNotifications } from './Notifications/SuperAdminUserNotifications';
import './SuperAdminUser.css';

type UserTab = 'overview' | 'permissions' | 'sessions' | 'notifications';

type OverviewTextField =
  | 'firstName'
  | 'lastName'
  | 'gender'
  | 'birthday'
  | 'email'
  | 'phone'
  | 'misEmail'
  | 'misPhone'
  | 'misId'
  | 'countOneTimeIosSessions'
  | 'countOneTimeAndroidSessions'
  | 'countOneTimeWebSessions'
  | 'blockedReason';

type OverviewCheckboxField = 'emailVerified' | 'smsVerified' | 'isPaymentsEnabled' | 'blocked';

interface Props {
  match: {
    params: {
      userId: string;
    };
  };
}

interface OverviewForm {
  firstName: string;
  lastName: string;
  gender: string;
  birthday: string;
  email: string;
  phone: string;
  misEmail: string;
  misPhone: string;
  misId: string;
  emailVerified: boolean;
  smsVerified: boolean;
  isPaymentsEnabled: boolean;
  countOneTimeIosSessions: string;
  countOneTimeAndroidSessions: string;
  countOneTimeWebSessions: string;
  blocked: boolean;
  blockedReason: string;
}

interface State {
  user?: SuperAdminUserModel;
  activeTab: UserTab;
  isLoading: boolean;
  isSavingOverview: boolean;
  isSavingChannel: boolean;
  isOverviewEditMode: boolean;
  isAddChannelFormOpen: boolean;
  availableChannelSchools: ChannelSchoolOption[];
  channelForm: ChannelForm;
  overviewForm: OverviewForm;
  errorMessage: string;
  successMessage: string;
  isMergeOpen: boolean;
  mergeSearch: string;
  mergeSchoolSearch: string;
  mergeSchoolId: string;
  mergeSchoolOptions: Array<{ id: string; name: string }>;
  mergeCandidates: SuperAdminUserModel[];
  mergePreview?: SuperAdminLoginMergePreview;
  isMergeLoading: boolean;
  isMergeConfirmed: boolean;
}

function getUserName(user: SuperAdminUserModel): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.email || user.id;
}

function formatDate(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getVerifiedText(value?: boolean): string {
  return value ? 'Yes' : 'No';
}

function formatValue(value?: string): string {
  return value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    : '';
}

function getChannelId(channel: any): string {
  return channel && (channel.id || channel._id) ? String(channel.id || channel._id) : '';
}

function getChannelType(channel: any): string {
  return channel && channel.type ? String(channel.type) : '';
}

function getChannelStatus(channel: any): string {
  return channel && channel.status ? String(channel.status) : '';
}

function getChannelBadgeClass(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'badge badge-success';
    case 'REMOVED':
      return 'badge badge-danger';
    case 'OVERRIDEN':
      return 'badge badge-warning';
    default:
      return 'badge badge-secondary';
  }
}

function getChannelTypeBadgeClass(type: string): string {
  switch (type) {
    case 'DEFAULT_EMAIL':
    case 'MIS_EMAIL':
      return 'badge badge-info';
    case 'DEFAULT_SMS':
    case 'MIS_SMS':
      return 'badge badge-primary';
    case 'IOS':
      return 'badge badge-dark';
    case 'ANDROID':
      return 'badge badge-secondary';
    default:
      return 'badge badge-light';
  }
}

function getChannelMainTitle(channel: any): string {
  const type = getChannelType(channel);
  const deviceName = channel && channel.deviceName ? String(channel.deviceName) : '';

  if (deviceName) {
    return deviceName;
  }

  if (type) {
    return formatValue(type);
  }

  return 'Notification channel';
}

function getChannelSubTitle(channel: any): string {
  const parts = [];

  if (channel && channel.environment) {
    parts.push(formatValue(String(channel.environment)));
  }

  if (channel && channel.schoolId) {
    parts.push(`school: ${channel.schoolId}`);
  }

  return parts.join(' / ');
}

type AddChannelType = 'DEFAULT_SMS' | 'MIS_EMAIL' | 'MIS_SMS';

interface ChannelSchoolOption {
  id: string;
  name: string;
}

interface ChannelForm {
  schoolId: string;
  type: AddChannelType | '';
}

function getEmptyChannelForm(): ChannelForm {
  return {
    schoolId: '',
    type: ''
  };
}

function isActiveChannel(channel: SuperAdminUserNotificationChannel): boolean {
  return !channel.status || channel.status === 'ACTIVE';
}

function isMobileChannel(channel: SuperAdminUserNotificationChannel): boolean {
  return channel.type === 'IOS' || channel.type === 'ANDROID';
}

function getChannelSchoolId(channel: SuperAdminUserNotificationChannel): string {
  return channel && channel.schoolId ? String(channel.schoolId) : '';
}

function findActiveChannel(
  channels: SuperAdminUserNotificationChannel[],
  type: string,
  schoolId?: string
): SuperAdminUserNotificationChannel | undefined {
  return channels.find(channel => {
    if (!isActiveChannel(channel) || channel.type !== type) {
      return false;
    }

    if (typeof schoolId === 'undefined') {
      return true;
    }

    return getChannelSchoolId(channel) === schoolId;
  });
}

function getAvailableChannelSchoolId(item: any): string {
  return item && (item.schoolId || item.id || item._id) ? String(item.schoolId || item.id || item._id) : '';
}

function getAvailableChannelSchoolName(item: any): string {
  if (item && item.projectName) {
    return String(item.projectName);
  }

  if (item && item.name) {
    return String(item.name);
  }

  return getAvailableChannelSchoolId(item);
}

function normalizeAvailableChannelSchools(items: any[]): ChannelSchoolOption[] {
  const result: ChannelSchoolOption[] = [];

  (items || []).forEach(item => {
    const id = getAvailableChannelSchoolId(item);

    if (!id || result.some(school => school.id === id)) {
      return;
    }

    result.push({
      id,
      name: getAvailableChannelSchoolName(item)
    });
  });

  return result.sort((a, b) => a.name.localeCompare(b.name));
}

function getAddChannelToken(type: string): string {
  switch (type) {
    case 'DEFAULT_SMS':
      return 'sms';
    case 'MIS_EMAIL':
      return 'mis_email';
    case 'MIS_SMS':
      return 'mis_sms';
    default:
      return 'channel';
  }
}

function getChannelTypeLabel(type: string): string {
  return formatValue(type);
}

function getEmptyOverviewForm(): OverviewForm {
  return {
    firstName: '',
    lastName: '',
    gender: '',
    birthday: '',
    email: '',
    phone: '',
    misEmail: '',
    misPhone: '',
    misId: '',
    emailVerified: false,
    smsVerified: false,
    isPaymentsEnabled: false,
    countOneTimeIosSessions: '',
    countOneTimeAndroidSessions: '',
    countOneTimeWebSessions: '',
    blocked: false,
    blockedReason: ''
  };
}

function getOverviewForm(user?: SuperAdminUserModel): OverviewForm {
  const verificationStatus =
    user && user.verification && user.verification.status ? user.verification.status : undefined;

  return {
    firstName: user && user.firstName ? user.firstName : '',
    lastName: user && user.lastName ? user.lastName : '',
    gender: user && user.gender ? user.gender : '',
    birthday: user && user.birthday ? user.birthday : '',
    email: user && user.email ? user.email : '',
    phone: user && user.phone ? user.phone : '',
    misEmail: user && user.misEmail ? user.misEmail : '',
    misPhone: user && user.misPhone ? user.misPhone : '',
    misId: user && user.misId ? user.misId : '',
    emailVerified: verificationStatus ? Boolean(verificationStatus.email) : false,
    smsVerified: verificationStatus ? Boolean(verificationStatus.sms) : false,
    isPaymentsEnabled: user && typeof user.isPaymentsEnabled === 'boolean' ? user.isPaymentsEnabled : false,
    countOneTimeIosSessions:
      user && typeof user.countOneTimeIosSessions === 'number' ? String(user.countOneTimeIosSessions) : '',
    countOneTimeAndroidSessions:
      user && typeof user.countOneTimeAndroidSessions === 'number' ? String(user.countOneTimeAndroidSessions) : '',
    countOneTimeWebSessions:
      user && typeof user.countOneTimeWebSessions === 'number' ? String(user.countOneTimeWebSessions) : '',
    blocked: user && user.status === 'BLOCKED',
    blockedReason: user && user.statusNotes ? user.statusNotes : ''
  };
}

function getNumberOrUndefined(value: string): number | undefined {
  if (value === '') {
    return undefined;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? undefined : numberValue;
}

export class SuperAdminUser extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      activeTab: 'overview',
      isLoading: false,
      isSavingOverview: false,
      isSavingChannel: false,
      isOverviewEditMode: false,
      isAddChannelFormOpen: false,
      availableChannelSchools: [],
      channelForm: getEmptyChannelForm(),
      overviewForm: getEmptyOverviewForm(),
      errorMessage: '',
      successMessage: '',
      isMergeOpen: false,
      mergeSearch: '',
      mergeSchoolSearch: '',
      mergeSchoolId: '',
      mergeSchoolOptions: [],
      mergeCandidates: [],
      isMergeLoading: false,
      isMergeConfirmed: false
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  loadUser = (): void => {
    const userId = this.props.match.params.userId;

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    Promise.all([getSuperAdminUser(userId), getSuperAdminUserAvailableNotificationChannelSchools(userId)])
      .then(([user, availableChannelSchools]) => {
        this.setState({
          user,
          availableChannelSchools: normalizeAvailableChannelSchools(availableChannelSchools),
          overviewForm: getOverviewForm(user),
          isLoading: false,
          errorMessage: ''
        });
      })
      .catch(error => {
        console.error('Can not load superadmin user', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load user. Please check the session and try again.'
        });
      });
  };

  openLoginMerge = (): void => {
    this.setState({
      isMergeOpen: true,
      mergeSearch: '',
      mergeSchoolSearch: '',
      mergeSchoolId: '',
      mergeSchoolOptions: [],
      mergeCandidates: [],
      mergePreview: undefined,
      isMergeConfirmed: false
    });
  };

  closeLoginMerge = (): void => {
    if (!this.state.isMergeLoading) this.setState({ isMergeOpen: false, mergePreview: undefined });
  };

  searchLoginMergeCandidates = (): void => {
    const sourceId = this.props.match.params.userId;
    if (!this.state.mergeSchoolId) return;
    this.setState({ isMergeLoading: true, errorMessage: '' });
    getSuperAdminLoginMergeCandidates(sourceId, this.state.mergeSearch, this.state.mergeSchoolId)
      .then(mergeCandidates => this.setState({ mergeCandidates, isMergeLoading: false }))
      .catch(() => this.setState({ isMergeLoading: false, errorMessage: 'Could not search merge candidates.' }));
  };

  mergeSchoolSearchTimer: any;

  onMergeSchoolSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const mergeSchoolSearch = event.target.value;
    this.setState({
      mergeSchoolSearch,
      mergeSchoolId: '',
      mergeSchoolOptions: [],
      mergeCandidates: [],
      mergePreview: undefined,
      isMergeConfirmed: false
    });
    clearTimeout(this.mergeSchoolSearchTimer);
    if (mergeSchoolSearch.trim().length < 2) return;
    this.mergeSchoolSearchTimer = setTimeout(() => {
      getSuperAdminSchools({
        where: { name: { like: mergeSchoolSearch, options: 'i' } },
        limit: 10,
        skip: 0,
        order: 'name ASC'
      })
        .then(schools =>
          this.setState({
            mergeSchoolOptions: schools.map(school => ({ id: school.id, name: school.name || '' }))
          })
        )
        .catch(() => this.setState({ errorMessage: 'Could not search schools.' }));
    }, 300);
  };

  selectMergeSchool = (school: { id: string; name: string }): void => {
    this.setState({
      mergeSchoolId: school.id,
      mergeSchoolSearch: school.name,
      mergeSchoolOptions: [],
      mergeCandidates: [],
      mergePreview: undefined,
      isMergeConfirmed: false
    });
  };

  selectLoginMergeTarget = (targetUserId: string): void => {
    this.setState({ isMergeLoading: true, mergePreview: undefined, isMergeConfirmed: false });
    previewSuperAdminLoginMerge(this.props.match.params.userId, targetUserId)
      .then(mergePreview => this.setState({ mergePreview, isMergeLoading: false }))
      .catch(() => this.setState({ isMergeLoading: false, errorMessage: 'Could not validate this merge.' }));
  };

  confirmLoginMerge = (): void => {
    const { mergePreview, isMergeConfirmed } = this.state;
    if (!mergePreview || !mergePreview.allowed || !isMergeConfirmed) return;
    this.setState({ isMergeLoading: true, errorMessage: '' });
    mergeSuperAdminLoginAccount(this.props.match.params.userId, mergePreview.target.id)
      .then(result =>
        this.setState({
          isMergeLoading: false,
          isMergeOpen: false,
          successMessage: `Login account merged into ${getUserName(result.user)}. ${
            result.channels.transferred
          } notification channel(s) transferred; ${result.channels.duplicatesRemoved} duplicate(s) removed.`
        })
      )
      .catch(error => {
        const text =
          error && error.response && error.response.data && error.response.data.details
            ? error.response.data.details.text
            : 'Could not merge the accounts.';
        this.setState({ isMergeLoading: false, errorMessage: text });
      });
  };

  renderLoginMerge() {
    const {
      user,
      isMergeOpen,
      mergeSearch,
      mergeSchoolSearch,
      mergeSchoolId,
      mergeSchoolOptions,
      mergeCandidates,
      mergePreview,
      isMergeLoading,
      isMergeConfirmed
    } = this.state;
    if (!user || !isMergeOpen) return null;
    return (
      <div className="SuperAdminUserMergePanel mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5>Merge login account</h5>
            <p className="text-muted mb-3">
              Move this account's login credentials and notification channels to the real role-bearing user. Official
              pupil/staff details and permissions stay on the destination user.
            </p>
          </div>
          <button type="button" className="close" onClick={this.closeLoginMerge} disabled={isMergeLoading}>
            ×
          </button>
        </div>
        <div className="form-group position-relative mb-3">
          <label className="font-weight-bold">School</label>
          <input
            className="form-control"
            value={mergeSchoolSearch}
            placeholder="Start typing a school name"
            onChange={this.onMergeSchoolSearch}
            autoComplete="off"
          />
          {mergeSchoolOptions.length > 0 && (
            <div className="list-group position-absolute w-100 SuperAdminUserMergeSchoolOptions">
              {mergeSchoolOptions.map(school => (
                <button
                  type="button"
                  className="list-group-item list-group-item-action"
                  key={school.id}
                  onMouseDown={() => this.selectMergeSchool(school)}
                >
                  {school.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <label className="font-weight-bold">Destination user</label>
        <div className="input-group mb-3">
          <input
            className="form-control"
            value={mergeSearch}
            placeholder="Search by name, MIS email or MIS ID"
            onChange={event => this.setState({ mergeSearch: event.target.value })}
            onKeyDown={event => {
              if (event.key === 'Enter') this.searchLoginMergeCandidates();
            }}
          />
          <div className="input-group-append">
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.searchLoginMergeCandidates}
              disabled={isMergeLoading || !mergeSchoolId}
            >
              Search
            </button>
          </div>
        </div>
        {!mergeSchoolId && (
          <div className="text-muted small mb-3">Select a school before searching for a destination user.</div>
        )}
        <div className="SuperAdminUserMergeCandidates">
          {mergeCandidates.map(candidate => {
            const roles = candidate.mergeSchool ? candidate.mergeSchool.roles : [];
            return (
              <button
                type="button"
                key={candidate.id}
                className="SuperAdminUserMergeCandidate"
                onClick={() => this.selectLoginMergeTarget(candidate.id)}
                disabled={isMergeLoading}
              >
                <div className="SuperAdminUserMergeCandidateHeader">
                  <strong>{getUserName(candidate)}</strong>
                  <span>DOB: {formatDate(candidate.birthday || undefined) || 'Not recorded'}</span>
                </div>
                <div className="SuperAdminUserMergeCandidateIdentity">
                  {candidate.misEmail || 'No MIS email'} · Permission MIS IDs:{' '}
                  {(candidate.permissions || [])
                    .map(permission => permission.misId)
                    .filter(Boolean)
                    .join(', ') || 'Not recorded'}{' '}
                  · User ID: {candidate.id}
                </div>
                <div className="SuperAdminUserMergeCandidateRoles">
                  {roles.map((role, index) => (
                    <span className="badge badge-light border" key={`${role.preset || 'role'}-${index}`}>
                      {formatValue(role.preset) || 'Role not recorded'}
                      {role.preset === 'STUDENT' &&
                        ` · Form: ${role.formName || 'Not assigned'} · House: ${role.houseName || 'Not assigned'}`}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
        {mergePreview && (
          <div className={`alert mt-3 ${mergePreview.allowed ? 'alert-warning' : 'alert-danger'}`}>
            <strong>
              {mergePreview.allowed ? `Merge into ${getUserName(mergePreview.target)}?` : 'This merge is blocked'}
            </strong>
            {mergePreview.blockers.map(blocker => (
              <div key={blocker}>{blocker}</div>
            ))}
            {mergePreview.allowed && (
              <>
                <div className="mt-2">Email: {mergePreview.transfer.email}</div>
                <div>Notification channels to process: {mergePreview.transfer.notificationChannels}</div>
                <label className="d-flex align-items-center mt-3 mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={isMergeConfirmed}
                    onChange={event => this.setState({ isMergeConfirmed: event.target.checked })}
                  />
                  I have checked both users and understand that this cannot be undone.
                </label>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!isMergeConfirmed || isMergeLoading}
                  onClick={this.confirmLoginMerge}
                >
                  {isMergeLoading ? 'Merging…' : 'Merge accounts'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  setActiveTab = (activeTab: UserTab) => (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    this.setState({ activeTab, successMessage: '', errorMessage: '' });
  };

  startOverviewEdit = (): void => {
    const { user } = this.state;
    this.setState({
      isOverviewEditMode: true,
      overviewForm: getOverviewForm(user),
      successMessage: '',
      errorMessage: ''
    });
  };

  cancelOverviewEdit = (): void => {
    const { user } = this.state;
    this.setState({
      isOverviewEditMode: false,
      overviewForm: getOverviewForm(user),
      successMessage: '',
      errorMessage: ''
    });
  };

  onOverviewTextChange = (field: OverviewTextField) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    const { overviewForm } = this.state;
    this.setState({
      overviewForm: {
        ...overviewForm,
        [field]: event.target.value
      }
    } as Pick<State, 'overviewForm'>);
  };

  onOverviewCheckboxChange = (field: OverviewCheckboxField) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { overviewForm } = this.state;
    this.setState({
      overviewForm: {
        ...overviewForm,
        [field]: event.target.checked
      }
    } as Pick<State, 'overviewForm'>);
  };

  saveOverview = (): void => {
    const { user, overviewForm } = this.state;

    if (!user) {
      return;
    }

    const userId = this.props.match.params.userId;
    const verificationStatus = user.verification && user.verification.status ? user.verification.status : {};
    const emailVerifiedBefore = Boolean(verificationStatus.email);
    const smsVerifiedBefore = Boolean(verificationStatus.sms);
    const blockedBefore = user.status === 'BLOCKED';

    const data: SuperAdminUserUpdateData = {
      firstName: overviewForm.firstName,
      lastName: overviewForm.lastName,
      gender: overviewForm.gender || null,
      birthday: overviewForm.birthday || null,
      email: overviewForm.email,
      phone: overviewForm.phone,
      misEmail: overviewForm.misEmail,
      misPhone: overviewForm.misPhone,
      misId: overviewForm.misId,
      isPaymentsEnabled: overviewForm.isPaymentsEnabled,
      countOneTimeIosSessions: getNumberOrUndefined(overviewForm.countOneTimeIosSessions),
      countOneTimeAndroidSessions: getNumberOrUndefined(overviewForm.countOneTimeAndroidSessions),
      countOneTimeWebSessions: getNumberOrUndefined(overviewForm.countOneTimeWebSessions)
    };

    const promises: Array<Promise<any>> = [updateSuperAdminUser(userId, data)];

    if (emailVerifiedBefore !== overviewForm.emailVerified) {
      promises.push(setSuperAdminUserEmailVerification(userId, overviewForm.emailVerified));
    }

    if (smsVerifiedBefore !== overviewForm.smsVerified) {
      promises.push(setSuperAdminUserSmsVerification(userId, overviewForm.smsVerified));
    }

    if (blockedBefore !== overviewForm.blocked) {
      promises.push(setSuperAdminUserBlock(userId, overviewForm.blocked, overviewForm.blockedReason));
    }

    this.setState({ isSavingOverview: true, errorMessage: '', successMessage: '' });

    Promise.all(promises)
      .then(() => getSuperAdminUser(userId))
      .then(updatedUser => {
        this.setState({
          user: updatedUser,
          overviewForm: getOverviewForm(updatedUser),
          isOverviewEditMode: false,
          isSavingOverview: false,
          successMessage: 'User details have been updated.'
        });
      })
      .catch(error => {
        console.error('Can not update superadmin user', error);
        this.setState({
          isSavingOverview: false,
          errorMessage: 'Can not update user details. Please check the data and try again.'
        });
      });
  };

  renderOverview() {
    const { user, isOverviewEditMode, overviewForm, isSavingOverview } = this.state;

    if (!user) {
      return null;
    }

    const emailVerified = user.verification && user.verification.status ? user.verification.status.email : false;
    const smsVerified = user.verification && user.verification.status ? user.verification.status.sms : false;
    const verificationTokens = user.verification && user.verification.tokens ? user.verification.tokens : {};
    const permissions = user.permissions || [];
    const activePermissions = permissions.filter(permission => permission.status === 'ACTIVE');

    if (isOverviewEditMode) {
      return (
        <div className="SuperAdminUserOverview">
          <div className="SuperAdminUserOverviewActions mb-3">
            <button
              type="button"
              className="btn btn-primary btn-sm mr-2"
              onClick={this.saveOverview}
              disabled={isSavingOverview}
            >
              {isSavingOverview ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={this.cancelOverviewEdit}
              disabled={isSavingOverview}
            >
              Cancel
            </button>
          </div>

          <div className="SuperAdminUserEditGrid">
            <label>First name</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.firstName}
              onChange={this.onOverviewTextChange('firstName')}
            />

            <label>Last name</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.lastName}
              onChange={this.onOverviewTextChange('lastName')}
            />

            <label>Email</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.email}
              onChange={this.onOverviewTextChange('email')}
            />

            <label>Phone</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.phone}
              onChange={this.onOverviewTextChange('phone')}
            />

            <label>Gender</label>
            <select
              className="form-control form-control-sm"
              value={overviewForm.gender}
              onChange={this.onOverviewTextChange('gender')}
            >
              <option value="">Not defined</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>

            <label>Birthday</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={overviewForm.birthday}
              onChange={this.onOverviewTextChange('birthday')}
            />

            <label>MIS email</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.misEmail}
              onChange={this.onOverviewTextChange('misEmail')}
            />

            <label>MIS phone</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.misPhone}
              onChange={this.onOverviewTextChange('misPhone')}
            />

            <label>Legacy user MIS ID</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.misId}
              onChange={this.onOverviewTextChange('misId')}
            />

            <label>One-time iOS sessions</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={overviewForm.countOneTimeIosSessions}
              onChange={this.onOverviewTextChange('countOneTimeIosSessions')}
            />

            <label>One-time Android sessions</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={overviewForm.countOneTimeAndroidSessions}
              onChange={this.onOverviewTextChange('countOneTimeAndroidSessions')}
            />

            <label>One-time Web sessions</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={overviewForm.countOneTimeWebSessions}
              onChange={this.onOverviewTextChange('countOneTimeWebSessions')}
            />

            <label>Email verified</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={overviewForm.emailVerified}
                onChange={this.onOverviewCheckboxChange('emailVerified')}
              />
            </div>

            <label>SMS verified</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={overviewForm.smsVerified}
                onChange={this.onOverviewCheckboxChange('smsVerified')}
              />
            </div>

            <label>Payments enabled</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={overviewForm.isPaymentsEnabled}
                onChange={this.onOverviewCheckboxChange('isPaymentsEnabled')}
              />
            </div>

            <label>Blocked</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={overviewForm.blocked}
                onChange={this.onOverviewCheckboxChange('blocked')}
              />
            </div>

            <label>Block reason</label>
            <input
              className="form-control form-control-sm"
              value={overviewForm.blockedReason}
              onChange={this.onOverviewTextChange('blockedReason')}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="SuperAdminUserOverview">
        <div className="SuperAdminUserOverviewActions mb-3">
          <button type="button" className="btn btn-primary btn-sm" onClick={this.startOverviewEdit}>
            Edit user
          </button>
          {activePermissions.length === 0 && user.status === 'ACTIVE' && user.email && (
            <button type="button" className="btn btn-outline-danger btn-sm ml-2" onClick={this.openLoginMerge}>
              Merge login account
            </button>
          )}
        </div>

        {this.renderLoginMerge()}

        <div className="SuperAdminUserInfoGrid">
          <div>
            <strong>Name</strong>
          </div>
          <div>{getUserName(user)}</div>
          <div>
            <strong>Email</strong>
          </div>
          <div>{user.email || ''}</div>
          <div>
            <strong>MIS email</strong>
          </div>
          <div>{user.misEmail || ''}</div>
          <div>
            <strong>Phone</strong>
          </div>
          <div>{user.phone || ''}</div>
          <div>
            <strong>MIS phone</strong>
          </div>
          <div>{user.misPhone || ''}</div>
          <div>
            <strong>Gender</strong>
          </div>
          <div>{formatValue(user.gender)}</div>
          <div>
            <strong>Birthday</strong>
          </div>
          <div>{formatDate(user.birthday)}</div>
          <div>
            <strong>Status</strong>
          </div>
          <div>{formatValue(user.status)}</div>
          <div>
            <strong>Status notes</strong>
          </div>
          <div>{user.statusNotes || ''}</div>
          <div>
            <strong>Blocked</strong>
          </div>
          <div>{getVerifiedText(user.status === 'BLOCKED')}</div>
          <div>
            <strong>Email verified</strong>
          </div>
          <div>{getVerifiedText(emailVerified)}</div>
          <div>
            <strong>Email verification code</strong>
          </div>
          <div>{verificationTokens.email || ''}</div>
          <div>
            <strong>Email code sent</strong>
          </div>
          <div>{formatDateTime(verificationTokens.emailSentAt)}</div>
          <div>
            <strong>SMS verified</strong>
          </div>
          <div>{getVerifiedText(smsVerified)}</div>
          <div>
            <strong>SMS verification code</strong>
          </div>
          <div>{verificationTokens.sms || ''}</div>
          <div>
            <strong>SMS code sent</strong>
          </div>
          <div>{formatDateTime(verificationTokens.smsSentAt)}</div>
          <div>
            <strong>Payments enabled</strong>
          </div>
          <div>{getVerifiedText(user.isPaymentsEnabled)}</div>
          <div>
            <strong>Legacy user MIS ID</strong>
          </div>
          <div>{user.misId || ''}</div>
          <div>
            <strong>One-time iOS sessions</strong>
          </div>
          <div>{typeof user.countOneTimeIosSessions === 'number' ? user.countOneTimeIosSessions : ''}</div>
          <div>
            <strong>One-time Android sessions</strong>
          </div>
          <div>{typeof user.countOneTimeAndroidSessions === 'number' ? user.countOneTimeAndroidSessions : ''}</div>
          <div>
            <strong>One-time Web sessions</strong>
          </div>
          <div>{typeof user.countOneTimeWebSessions === 'number' ? user.countOneTimeWebSessions : ''}</div>
          <div>
            <strong>Permissions</strong>
          </div>
          <div>
            {permissions.length} ({activePermissions.length} active)
          </div>
          <div>
            <strong>Created</strong>
          </div>
          <div>{formatDateTime(user.createdAt)}</div>
          <div>
            <strong>Updated</strong>
          </div>
          <div>{formatDateTime(user.updatedAt)}</div>
        </div>

        {this.renderNotificationChannels()}
      </div>
    );
  }

  refreshUserAfterChannelChange = (successMessage: string): Promise<void> => {
    const userId = this.props.match.params.userId;

    return getSuperAdminUser(userId).then(user => {
      this.setState({
        user,
        overviewForm: getOverviewForm(user),
        isSavingChannel: false,
        isAddChannelFormOpen: false,
        channelForm: getEmptyChannelForm(),
        successMessage
      });
    });
  };

  setDefaultEmailEnabled = (enabled: boolean): void => {
    const { user } = this.state;

    if (!user) {
      return;
    }

    const userId = this.props.match.params.userId;
    const channels = user.notificationChannelsData || [];
    const channel = findActiveChannel(channels, 'DEFAULT_EMAIL');

    if (!enabled && !channel) {
      return;
    }

    const actionPromise: Promise<any> = enabled
      ? createSuperAdminUserNotificationChannel(userId, {
          type: 'DEFAULT_EMAIL',
          token: 'email'
        })
      : deleteSuperAdminUserNotificationChannel(userId, getChannelId(channel));

    this.setState({ isSavingChannel: true, errorMessage: '', successMessage: '' });

    actionPromise
      .then(() =>
        this.refreshUserAfterChannelChange(`Default email channel has been ${enabled ? 'enabled' : 'disabled'}.`)
      )
      .catch(error => {
        console.error('Can not update default email channel', error);
        this.setState({
          isSavingChannel: false,
          errorMessage: 'Can not update default email channel. Please try again.'
        });
      });
  };

  onAddChannelClick = (): void => {
    this.setState({
      isAddChannelFormOpen: true,
      channelForm: getEmptyChannelForm(),
      errorMessage: '',
      successMessage: ''
    });
  };

  onCancelAddChannelClick = (): void => {
    this.setState({
      isAddChannelFormOpen: false,
      channelForm: getEmptyChannelForm(),
      errorMessage: ''
    });
  };

  onChannelFormChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    const nextForm = {
      ...this.state.channelForm,
      [name]: value
    } as ChannelForm;

    if (name === 'schoolId') {
      nextForm.type = '';
    }

    this.setState({
      channelForm: nextForm,
      errorMessage: '',
      successMessage: ''
    });
  };

  getAvailableAddChannelTypes(): AddChannelType[] {
    const { user, channelForm } = this.state;

    if (!user || !channelForm.schoolId) {
      return [];
    }

    const channels = user.notificationChannelsData || [];
    const result: AddChannelType[] = [];

    if (!findActiveChannel(channels, 'DEFAULT_SMS', channelForm.schoolId)) {
      result.push('DEFAULT_SMS');
    }

    if (!findActiveChannel(channels, 'MIS_EMAIL', channelForm.schoolId)) {
      result.push('MIS_EMAIL');
    }

    if (!findActiveChannel(channels, 'MIS_SMS', channelForm.schoolId)) {
      result.push('MIS_SMS');
    }

    return result;
  }

  onSaveAddChannelClick = (): void => {
    const { channelForm } = this.state;

    if (!channelForm.schoolId) {
      this.setState({ errorMessage: 'Please select school.' });
      return;
    }

    if (!channelForm.type) {
      this.setState({ errorMessage: 'Please select channel type.' });
      return;
    }

    this.setState({ isSavingChannel: true, errorMessage: '', successMessage: '' });

    createSuperAdminUserNotificationChannel(this.props.match.params.userId, {
      type: channelForm.type,
      schoolId: channelForm.schoolId,
      token: getAddChannelToken(channelForm.type)
    })
      .then(() =>
        this.refreshUserAfterChannelChange(`${getChannelTypeLabel(channelForm.type)} channel has been added.`)
      )
      .catch(error => {
        console.error('Can not add notification channel', error);
        this.setState({
          isSavingChannel: false,
          errorMessage: 'Can not add notification channel. Please check fields and try again.'
        });
      });
  };

  removeChannel = (channel: SuperAdminUserNotificationChannel): void => {
    const channelId = getChannelId(channel);
    const type = getChannelType(channel);

    if (!channelId) {
      return;
    }

    const isMobile = isMobileChannel(channel);
    const confirmText = isMobile
      ? 'Remove this mobile notification channel? The user will stop receiving push notifications on this device.'
      : `Disable ${formatValue(type)} notification channel?`;

    if (!window.confirm(confirmText)) {
      return;
    }

    this.setState({ isSavingChannel: true, errorMessage: '', successMessage: '' });

    deleteSuperAdminUserNotificationChannel(this.props.match.params.userId, channelId)
      .then(() => this.refreshUserAfterChannelChange(`${formatValue(type)} channel has been removed.`))
      .catch(error => {
        console.error('Can not remove notification channel', error);
        this.setState({
          isSavingChannel: false,
          errorMessage: 'Can not remove notification channel. Please try again.'
        });
      });
  };

  renderDefaultEmailControl(): React.ReactNode {
    const { user, isSavingChannel } = this.state;

    if (!user) {
      return null;
    }

    const channels = user.notificationChannelsData || [];
    const defaultEmailChannel = findActiveChannel(channels, 'DEFAULT_EMAIL');
    const isEnabled = Boolean(defaultEmailChannel);

    return (
      <div className="SuperAdminUserDefaultChannelRow">
        <div>
          <strong>Default email</strong>
          <div className="text-muted small">{user.email || 'No email on user profile'}</div>
        </div>
        <button
          type="button"
          className={isEnabled ? 'btn btn-outline-danger btn-sm' : 'btn btn-outline-success btn-sm'}
          onClick={() => this.setDefaultEmailEnabled(!isEnabled)}
          disabled={isSavingChannel || (!isEnabled && !user.email)}
        >
          {isEnabled ? 'Disable' : 'Enable'}
        </button>
      </div>
    );
  }

  renderAddChannelForm(): React.ReactNode {
    const { availableChannelSchools, channelForm, isSavingChannel } = this.state;
    const types = this.getAvailableAddChannelTypes();

    if (!this.state.isAddChannelFormOpen) {
      return null;
    }

    return (
      <div className="SuperAdminUserAddChannelForm mt-3">
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>School</label>
            <select
              className="form-control"
              name="schoolId"
              value={channelForm.schoolId}
              onChange={this.onChannelFormChange}
              disabled={isSavingChannel}
            >
              <option value="">Select school</option>
              {availableChannelSchools.map(school => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
            {availableChannelSchools.length === 0 && (
              <small className="form-text text-muted">
                No schools with available notification subscription were found for this user.
              </small>
            )}
          </div>
          <div className="form-group col-md-6">
            <label>Channel type</label>
            <select
              className="form-control"
              name="type"
              value={channelForm.type}
              onChange={this.onChannelFormChange}
              disabled={!channelForm.schoolId || isSavingChannel}
            >
              <option value="">Select type</option>
              {types.map(type => (
                <option key={type} value={type}>
                  {getChannelTypeLabel(type)}
                </option>
              ))}
            </select>
            {channelForm.schoolId && types.length === 0 && (
              <small className="form-text text-muted">All available school-based channels already exist.</small>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm mr-2"
          onClick={this.onSaveAddChannelClick}
          disabled={isSavingChannel}
        >
          {isSavingChannel ? 'Saving...' : 'Add channel'}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={this.onCancelAddChannelClick}
          disabled={isSavingChannel}
        >
          Cancel
        </button>
      </div>
    );
  }

  renderNotificationChannels(): React.ReactNode {
    const { user, isSavingChannel } = this.state;
    const channels = user && Array.isArray(user.notificationChannelsData) ? user.notificationChannelsData : [];

    return (
      <div className="SuperAdminUserNotificationChannels mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Notification channels</h5>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={this.onAddChannelClick}
            disabled={isSavingChannel || this.state.isAddChannelFormOpen}
          >
            Add channel
          </button>
        </div>

        {this.renderDefaultEmailControl()}
        {this.renderAddChannelForm()}

        {channels.length === 0 && (
          <div className="alert alert-light border mt-3">No notification channels found for this user.</div>
        )}

        {channels.length > 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-sm table-bordered SuperAdminUserChannelTable">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Status</th>
                  <th>School</th>
                  <th>Device</th>
                  <th>Environment</th>
                  <th>App ID</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((channel, index) => {
                  const id = getChannelId(channel);
                  const type = getChannelType(channel);
                  const status = getChannelStatus(channel);
                  const canRemove =
                    isActiveChannel(channel) &&
                    (isMobileChannel(channel) || type === 'DEFAULT_SMS' || type === 'MIS_SMS' || type === 'MIS_EMAIL');

                  return (
                    <tr key={`notification_channel_${id || index}`}>
                      <td>
                        <span className={getChannelTypeBadgeClass(type)}>{formatValue(type)}</span>
                      </td>
                      <td>
                        {status ? <span className={getChannelBadgeClass(status)}>{formatValue(status)}</span> : ''}
                      </td>
                      <td>{getChannelSchoolId(channel)}</td>
                      <td>{channel.deviceName || ''}</td>
                      <td>{channel.environment || ''}</td>
                      <td>{channel.appId || ''}</td>
                      <td>{channel.updatedAt ? formatDateTime(channel.updatedAt) : ''}</td>
                      <td>
                        {type === 'DEFAULT_EMAIL' && isActiveChannel(channel) ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => this.setDefaultEmailEnabled(false)}
                            disabled={isSavingChannel}
                          >
                            Disable
                          </button>
                        ) : canRemove ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => this.removeChannel(channel)}
                            disabled={isSavingChannel}
                          >
                            {isMobileChannel(channel) ? 'Remove' : 'Disable'}
                          </button>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <details className="mt-3">
          <summary className="text-muted">Raw notification channels JSON</summary>
          <pre className="SuperAdminUserJsonBlock mt-2">{JSON.stringify(channels, null, 2)}</pre>
        </details>
      </div>
    );
  }

  renderTabContent() {
    const { activeTab } = this.state;
    const userId = this.props.match.params.userId;

    switch (activeTab) {
      case 'overview':
        return this.renderOverview();
      case 'permissions':
        return <SuperAdminUserPermissions userId={userId} />;
      case 'sessions':
        return <SuperAdminUserSessions userId={userId} />;
      case 'notifications':
        return <SuperAdminUserNotifications userId={userId} />;
      default:
        return null;
    }
  }

  renderTab(tab: UserTab, title: string) {
    const { activeTab } = this.state;
    const className = activeTab === tab ? 'nav-link active' : 'nav-link';

    return (
      <li className="nav-item">
        <a href={`#${tab}`} className={className} onClick={this.setActiveTab(tab)}>
          {title}
        </a>
      </li>
    );
  }

  render() {
    const { user, isLoading, errorMessage, successMessage } = this.state;

    return (
      <div className="container-fluid mt-3 SuperAdminUser">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Link to="/users" className="btn btn-secondary btn-sm mb-2">
              Back to users
            </Link>
            <h3 className="mb-0">{user ? getUserName(user) : 'User'}</h3>
            {user && <div className="text-muted">{user.email || user.id}</div>}
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}
        {isLoading && <Loader />}

        {!isLoading && user && (
          <>
            <ul className="nav nav-tabs mb-3">
              {this.renderTab('overview', 'Overview')}
              {this.renderTab('permissions', 'Permissions')}
              {this.renderTab('sessions', 'Sessions')}
              {this.renderTab('notifications', 'Notifications')}
            </ul>

            <div className="SuperAdminUserTabContent">{this.renderTabContent()}</div>
          </>
        )}
      </div>
    );
  }
}
